import * as React from 'react';
import clsx from 'clsx';
import {
  InputAdornment,
  MenuItem,
  Select,
  SelectChangeEvent,
} from '@mui/material';
import { InputSelect } from 'components/Form/InputSelect';
import { IOption, optionNone } from 'common/ui/interfaces/selectors';
import { IVerificationResultByRecognition } from 'common/ui/interfaces/verification-result-by-recognition';
import { VerificationResultByRecognition } from 'common/ui/constants/verification-result-by-recognition';
import { StatusTooltipTemplate } from '../Tooltip/template';
import { StatusTooltip } from '../Tooltip';

import { ReactComponent as CheckmarkIcon } from 'assets/images/input-checkmark.svg';
import { ReactComponent as WarningIcon } from 'assets/images/warning.svg';

import styles from './styles.module.scss';

interface InputProps {
  id: string;
  label: string;
  error: boolean;
  verificationResult?: IVerificationResultByRecognition;
  value: string[];
  onChange: (v: string[]) => void;
  options: IOption[];
  verified?: boolean;
  hideVerification?: boolean;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

export const MultipleSelectWithStatus = ({
  id,
  label,
  value,
  verified,
  onChange,
  error = false,
  verificationResult = VerificationResultByRecognition,
  options,
  hideVerification = false,
  onMouseEnter,
  onMouseLeave,
}: InputProps) => {
  const handleChange = (event: SelectChangeEvent<string[]>) => {
    const {
      target: { value },
    } = event;

    const newValue = (
      typeof value === 'string' ? value.split(', ') : value
    ).filter((v) => v !== optionNone);
    onChange(newValue.length ? newValue : [optionNone]);
  };
  return (
    <Select
      multiple
      variant="filled"
      value={value?.length ? value : [optionNone]}
      onChange={handleChange}
      labelId={id}
      id={id}
      defaultValue={[optionNone]}
      input={
        <InputSelect
          id={id}
          label={label}
          error={error}
          endAdornment={
            hideVerification ? undefined : (
              <InputAdornment
                position="end"
                classes={{
                  outlined: styles.adornedEnd,
                }}
              >
                {verified ? (
                  <CheckmarkIcon />
                ) : (
                  <StatusTooltip
                    title={
                      <StatusTooltipTemplate
                        title={verificationResult.title}
                        text={verificationResult.text}
                      />
                    }
                  >
                    <WarningIcon />
                  </StatusTooltip>
                )}
              </InputAdornment>
            )
          }
        />
      }
      classes={{
        select: styles.select,
        icon: styles.icon,
        iconOpen: styles.iconOpen,
      }}
      className={clsx(
        !verified && styles.unverified,
        verified && styles.verified
      )}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <MenuItem disabled value={optionNone}>
        {optionNone}
      </MenuItem>
      {options.map((o) => (
        <MenuItem
          key={o.value}
          value={o.value}
          classes={{ selected: styles.selected }}
        >
          {o.label}
        </MenuItem>
      ))}
    </Select>
  );
};
